import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { createFollowNotification } from "@/utils/notification-utils";

export interface Profile {
  id: string;
  full_name: string | null;
  username: string | null;
  avatar_url: string | null;
  bio: string | null;
  karma_points: number | null;
  interests: string | null;
  grade_level: string | null;
  country: string | null;
  state: string | null;
}

export const useUserProfilesData = () => {
  const [users, setUsers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [followingMap, setFollowingMap] = useState<Record<string, boolean>>({});
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      console.log("Fetching registered users (useUserProfilesData)... Current user ID:", user?.id);
      const currentUserId = user?.id;

      const query = supabase
        .from('profiles')
        .select('id, full_name, username, avatar_url, bio, karma_points, interests, grade_level, country, state')
        .not('username', 'is', null)
        .order('created_at', { ascending: false })
        .limit(20);

      if (currentUserId) {
        query.not('id', 'eq', currentUserId);
      }
        
      const { data, error } = await query;
      
      console.log("Supabase fetchUsers API response:", { data_count: data?.length, error });

      if (error) {
        console.error("Error fetching users from Supabase:", error.message, error.details, error.hint);
        throw error;
      }
      
      console.log("Registered users processed:", data?.length || 0, "users found.");
      setUsers((data as Profile[]) || []);
    } catch (error: any) {
      console.error("Error in fetchUsers catch block:", error.message); 
      toast({
        title: "Error Fetching Users",
        description: "Could not retrieve user profiles at this time. Please try refreshing the list.",
        variant: "destructive",
      });
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  const fetchFollowingStatus = useCallback(async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_follows')
        .select('following_id')
        .eq('follower_id', user.id);
        
      if (error) throw error;
      
      const followMap: Record<string, boolean> = {};
      data?.forEach(follow => {
        followMap[follow.following_id] = true;
      });
      
      setFollowingMap(followMap);
    } catch (error) {
      console.error("Error fetching following status:", error);
    }
  }, [user]);

  useEffect(() => {
    fetchUsers();
    if (user) {
      fetchFollowingStatus();
    } else {
      setFollowingMap({});
    }
  }, [user, fetchUsers, fetchFollowingStatus]);

  const handleFollow = async (userId: string) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to follow users.",
        variant: "destructive",
      });
      return;
    }
    try {
      if (followingMap[userId]) {
        // Unfollow
        const { error } = await supabase
          .from('user_follows')
          .delete()
          .eq('follower_id', user.id)
          .eq('following_id', userId);
          
        if (error) throw error;
        
        setFollowingMap(prev => ({ ...prev, [userId]: false }));
        toast({ title: "Unfollowed", description: "You are no longer following this user." });
      } else {
        // Follow
        const { error } = await supabase
          .from('user_follows')
          .insert({ follower_id: user.id, following_id: userId });
          
        if (error) throw error;
        
        setFollowingMap(prev => ({ ...prev, [userId]: true }));
        
        // Get follower name for notification
        const { data: profileData } = await supabase
          .from('profiles')
          .select('full_name, username')
          .eq('id', user.id)
          .single();
          
        const followerName = profileData?.full_name || profileData?.username || 'Someone';
        await createFollowNotification(userId, followerName);
        
        toast({ title: "Following", description: "You are now following this user." });
      }
    } catch (error) {
      console.error("Error updating follow status:", error);
      toast({ title: "Error", description: "Could not update follow status.", variant: "destructive" });
    }
  };

  const handleMessage = async (userId: string, username: string | null) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to message users.",
        variant: "destructive",
      });
      return;
    }
    
    // Check if they follow each other (mutual connection)
    try {
      const [followingUser, userFollowingBack] = await Promise.all([
        supabase
          .from('user_follows')
          .select('*')
          .eq('follower_id', user.id)
          .eq('following_id', userId)
          .single(),
        supabase
          .from('user_follows')
          .select('*')
          .eq('follower_id', userId)
          .eq('following_id', user.id)
          .single()
      ]);

      if (!followingUser.data || !userFollowingBack.data) {
        toast({
          title: "Cannot Message",
          description: "You can only message users who follow you back (mutual connections).",
          variant: "destructive",
        });
        return;
      }

      // Navigate to messages page with the user ID
      window.location.href = `/messages/${userId}`;
    } catch (error) {
      console.error("Error checking mutual connection:", error);
      toast({
        title: "Error",
        description: "Could not verify connection status.",
        variant: "destructive",
      });
    }
  };

  const handleViewProfile = (userId: string) => {
    // This will be handled by the parent component
    return userId;
  };

  return { 
    users, 
    loading, 
    followingMap, 
    user, 
    handleFollow, 
    handleMessage, 
    handleViewProfile,
    fetchUsers 
  };
};
